import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Checkbox, MultiSelect, Select, SimpleGrid, Stack, Text, TextInput, Textarea, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const CreateJobPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const experienceOptions = [
    { value: '0_1', label: t('experienceRanges.zeroToOne') },
    { value: '2_3', label: t('experienceRanges.twoToThree') },
    { value: '4_6', label: t('experienceRanges.fourToSix') },
    { value: '7_PLUS', label: t('experienceRanges.sevenPlus') },
  ];

  const careerOptions = [
    { value: 'MUSICOTERAPIA', label: t('careers.musicotherapy') },
    { value: 'PSICOLOGIA', label: t('careers.psychology') },
    { value: 'PROFESORADO_PSICOLOGIA', label: t('careers.psychologyTeaching') },
    { value: 'TERAPIA_OCUPACIONAL', label: t('careers.occupationalTherapy') },
  ];

  const positionOptions = [
    { value: 'THERAPEUTIC_ASSISTANT', label: t('jobCategories.therapeuticAssistant') },
    { value: 'SCHOOL_ASSISTANT', label: t('jobCategories.schoolAssistant') },
    { value: 'HR', label: t('jobCategories.hr') },
    { value: 'PSYCHOLOGY_PRO', label: t('jobCategories.psychologyProfessional') },
    { value: 'DOCENTE_TUTOR', label: t('jobCategories.docenteTutor') },
    { value: 'MUSICOTERAPEUTA', label: t('jobCategories.musicoterapeuta') },
    { value: 'TERAPISTA_OCUPACIONAL', label: t('jobCategories.terapistaOcupacional') },
    { value: 'SELECTOR_PERSONAL', label: t('jobCategories.selectorPersonal') },
    { value: 'PERSONAL_ADMINISTRATIVO', label: t('jobCategories.personalAdministrativo') },
    { value: 'ORIENTADOR_SALA', label: t('jobCategories.orientadorSala') },
    { value: 'ACOMPANANTE_GERONTOLOGICO', label: t('jobCategories.acompananteGerontologico') },
    { value: 'COORDINADOR_INTEGRACIONES', label: t('jobCategories.coordinadorIntegraciones') },
    { value: 'SUPERVISOR_ATS', label: t('jobCategories.supervisorAts') },
    { value: 'OTHER', label: t('jobCategories.other') },
  ];

  const normalizePositionValue = (value: string) =>
    positionOptions.find((option) => option.value === value || option.label === value)?.value || value;

  const resolvePositionLabel = (value: string) =>
    positionOptions.find((option) => option.value === value)?.label || value;

  const isOtherSelected = (value: string) => normalizePositionValue(value) === 'OTHER';

  const [form, setForm] = useState(() => ({
    title: resolvePositionLabel('THERAPEUTIC_ASSISTANT'),
    company_name: '',
    location: '',
    is_remote: false,
    job_type: 'FULL_TIME',
    experience_range: '0_1',
    short_description: '',
    description: '',
    requirements: '',
    requires_at_title: false,
    requires_at_diploma_uba: false,
    requires_graduated: false,
    requires_school_inclusion_course: false,
    requires_hr_courses: false,
    careers_affine: [] as string[],
    position_option: 'THERAPEUTIC_ASSISTANT',
    position_other: ''
  }));

  if (user?.user_type === 'JOB_SEEKER') {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Title order={3}>{t('common.accessRestricted')}</Title>
        <Text c="dimmed">{t('common.onlyPosters')}</Text>
      </Card>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    if (form.careers_affine.length === 0) {
      setError(t('createJob.careersRequired'));
      setLoading(false);
      return;
    }

    try {
      const normalizedPosition = normalizePositionValue(form.position_option);
      const titleValue = normalizedPosition === 'OTHER'
        ? form.position_other.trim()
        : resolvePositionLabel(normalizedPosition);

      await api.createJob({
        title: titleValue,
        company_name: form.company_name,
        location: form.location,
        is_remote: form.is_remote,
        job_type: form.job_type,
        experience_range: form.experience_range,
        short_description: form.short_description,
        description: form.description,
        requirements: form.requirements,
        requires_at_title: form.requires_at_title,
        requires_at_diploma_uba: form.requires_at_diploma_uba,
        requires_graduated: form.requires_graduated,
        requires_school_inclusion_course: form.requires_school_inclusion_course,
        requires_hr_courses: form.requires_hr_courses,
        careers_affine: form.careers_affine,
      });

      setSuccess(true);
      setTimeout(() => navigate('/jobs/mine'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job posting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card withBorder padding="xl" radius="lg" shadow="sm">
      <Stack gap="md">
        <Title order={2}>{t('createJob.title')}</Title>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {error}
          </Alert>
        )}

        {success && (
          <Alert icon={<IconCheck size={16} />} title="Success" color="green" radius="md">
            Job posted successfully! Redirecting to your jobs...
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Select
                label={t('createJob.fields.title')}
                value={normalizePositionValue(form.position_option)}
                onChange={(value) => {
                  if (!value) return;
                  const normalizedValue = normalizePositionValue(value);
                  if (normalizedValue === 'OTHER') {
                    setForm((prev) => ({
                      ...prev,
                      position_option: normalizedValue,
                      title: prev.position_other.trim(),
                    }));
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    position_option: normalizedValue,
                    position_other: '',
                    title: resolvePositionLabel(normalizedValue),
                  }));
                }}
                data={positionOptions}
                required
                disabled={loading || success}
              />
              {isOtherSelected(form.position_option) ? (
                <TextInput
                  label={t('createJob.fields.categoryOther')}
                  value={form.position_other}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      position_option: 'OTHER',
                      position_other: value,
                      title: value,
                    }));
                  }}
                  disabled={loading || success}
                  required
                />
              ) : (
                <TextInput
                  label={t('createJob.fields.company')}
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  required
                  disabled={loading || success}
                />
              )}
              {isOtherSelected(form.position_option) && (
                <TextInput
                  label={t('createJob.fields.company')}
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  required
                  disabled={loading || success}
                />
              )}
              <TextInput
                label={t('createJob.fields.location')}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                required
                disabled={loading || success}
              />
              <Select
                label={t('createJob.fields.jobType')}
                value={form.job_type}
                onChange={(value) => value && setForm({ ...form, job_type: value })}
                data={[
                  { value: 'FULL_TIME', label: 'Full Time' },
                  { value: 'PART_TIME', label: 'Part Time' },
                  { value: 'INTERNSHIP', label: 'Pasante' },
                  { value: 'TEMPORARY', label: 'Temporal' },
                ]}
                disabled={loading || success}
              />
              <Select
                label={t('createJob.fields.experience')}
                value={form.experience_range}
                onChange={(value) => value && setForm({ ...form, experience_range: value })}
                data={experienceOptions}
                disabled={loading || success}
              />
              <Checkbox
                label={t('createJob.fields.remote')}
                checked={form.is_remote}
                onChange={(e) => setForm({ ...form, is_remote: e.currentTarget.checked })}
                disabled={loading || success}
              />
            </SimpleGrid>
            <Textarea
              label={t('createJob.fields.shortDescription')}
              value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              autosize
              minRows={2}
              maxLength={120}
              required
              disabled={loading || success}
            />
            <Textarea
              label={t('createJob.fields.description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              autosize
              minRows={4}
              required
              disabled={loading || success}
            />
            <Stack gap="xs">
              <Text fw={600}>{t('createJob.requirementsTitle')}</Text>
              <Checkbox
                label={t('createJob.fields.requiresAtCourse')}
                checked={form.requires_at_title}
                onChange={(e) => setForm({ ...form, requires_at_title: e.currentTarget.checked })}
                disabled={loading || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresAtDiplomaUba')}
                checked={form.requires_at_diploma_uba}
                onChange={(e) => setForm({ ...form, requires_at_diploma_uba: e.currentTarget.checked })}
                disabled={loading || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresDegreeTitle')}
                checked={form.requires_graduated}
                onChange={(e) => setForm({ ...form, requires_graduated: e.currentTarget.checked })}
                disabled={loading || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresSchoolInclusionCourse')}
                checked={form.requires_school_inclusion_course}
                onChange={(e) => setForm({ ...form, requires_school_inclusion_course: e.currentTarget.checked })}
                disabled={loading || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresHrCourses')}
                checked={form.requires_hr_courses}
                onChange={(e) => setForm({ ...form, requires_hr_courses: e.currentTarget.checked })}
                disabled={loading || success}
              />
              <Textarea
                label={t('createJob.fields.otherSkills')}
                value={form.requirements}
                onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                autosize
                minRows={2}
                disabled={loading || success}
              />
            </Stack>
            <MultiSelect
              label={t('createJob.fields.careers')}
              data={careerOptions}
              value={form.careers_affine}
              onChange={(value) => setForm({ ...form, careers_affine: value })}
              placeholder={t('createJob.fields.careersPlaceholder')}
              required
              disabled={loading || success}
            />
            <Button
              type="submit"
              radius="md"
              size="md"
              loading={loading}
              disabled={success}
            >
              {t('createJob.submit')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Card>
  );
};

export default CreateJobPage;
